import { useMediaQuery } from "@mui/material";
import badge from "assets/badges/yellow-badge.png";
import LPIcon from "assets/loyalty-icon-single-v2.png";
import BonusFlagComponent from "components/BonusFlag";
import ThemeButton from "components/Buttons/ThemeButton";
import CountdownV2 from "components/CountdownV2";
import { DisplayReward } from "components/DIP/types";
import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import usePopUpsV2 from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { useGoogleAnalyticsContext, useWebStoreContext } from "contexts";
import {
	IShopPurchaseOfferSingleItem,
	PurchaseOffers
} from "contexts/WebStoreProvider/types";
import { useAppDispatch, useAppSelector } from "hooks";
import useRewardsById from "hooks/useRewardsById";
import { sortBundleRewards } from "helpers/rewardSort";
import { WsDialogs } from "interfaces";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { featureTrackerAPI, offersApi } from "redux/apis";
import { useUpdateCooldownMutation } from "redux/apis/bundleUp";
import {
	getLimitedDeals,
	onDIPtrigger,
	onOpenPopUp,
	onSetBundleUpDip,
	onSetTimers,
	selectBundleUpState,
	// selectPoisBountyV3State,
	selectWebStoreState
} from "redux/slices";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { RootState } from "redux/store";
import { BestValueBadge } from "styles/general_styles";
import { InfoIcon } from "utils/svgIcons";
import { DataOverlay } from "./lib/DataOverlay";
import FinalPriceSVG from "./lib/FinalPriceSVG/index";
import {
	bannerStyle,
	Body,
	Container,
	Content,
	// ContributeToPB,
	InfoButton,
	OfferContainer,
	RewardDescription,
	SubTitle,
	textStyle,
	Title
} from "./styles";
import { FeatureType } from "interfaces/feature-tracker";
import { getTimeLeft } from "utils/time";

interface BundleUpProps {
	id: string;
}

export const BundleUp = ({ id }: BundleUpProps) => {
	const descriptionCharLength = 33; // Number of characters to show in the base offer description
	const dispatch = useAppDispatch();
	const { open, close } = usePopUpsV2();
	const { openDialog } = useDialogModalRedux();
	const { endTime, dip } = useAppSelector(selectBundleUpState);
	const isPortrait = useMediaQuery("(orientation: portrait)");
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	const showOfferId = useSelector(
		(state: RootState) => state.debug.showDebugLabels
	);
	const scale = useCalculateScale(
		isPortrait ? 470 + 50 : 1007 + 50,
		isPortrait ? 585 + 50 : 470 + 50
	);
	// Temporarily hidden per product request; restore this state with the JSX below
	// to re-enable the Poi's Bounty messaging.
	// const { canContribute: isPoisActive } = useAppSelector(
	// 	selectPoisBountyV3State
	// );
	const {
		authToken,
		handleRefresh,
		nowTime,
		postUserMetadata,
		suppressOrderConfirmation,
		userTracker,
		wsStoreBuy
	} = useWebStoreContext();
	const [
		UpdateCooldown,
		{ isLoading: isLoadingUpdateCooldown, isSuccess: isSuccessUpdateCooldown }
	] = useUpdateCooldownMutation();
	const {
		data: purchaseOffers
	}: {
		data?: PurchaseOffers;
	} = offersApi.endpoints.getOffers.useQueryState({
		offerType: "singleItem",
		isAuthenticated: !!authToken?.value
	});
	const { data: featuredDealsData } =
		offersApi.endpoints.getOffers.useQueryState({
			offerType: "featured",
			isAuthenticated: !!authToken?.value
		});
	const { data: eliteDealsData } = offersApi.endpoints.getOffers.useQueryState({
		offerType: "elite",
		isAuthenticated: !!authToken?.value
	});

	const allOffers = useMemo(
		() => [
			...(purchaseOffers?.offers ?? []),
			...(featuredDealsData?.offers ?? []),
			...(eliteDealsData?.offers ?? [])
		],
		[purchaseOffers?.offers, featuredDealsData?.offers, eliteDealsData?.offers]
	);

	const baseData = useMemo(() => {
		const offer = allOffers.find((o) => o?.id === id);
		if (
			offer?.upsellOfferConfig?.timerDurationMinutes &&
			(!endTime || (endTime && nowTime && nowTime > moment(endTime)))
		) {
			dispatch(
				onSetTimers({
					endTime: moment(nowTime)
						.add(offer?.upsellOfferConfig?.timerDurationMinutes, "minutes")
						.toISOString()
				})
			);
		}
		return offer;
	}, [allOffers, dispatch, endTime, id, nowTime]);

	const bundleUpData = useMemo(() => {
		return allOffers.find(
			(o) => o?.id === baseData?.upsellOfferConfig?.upsellOfferId
		);
	}, [allOffers, baseData?.upsellOfferConfig?.upsellOfferId]);

	const baseRewards = useRewardsById(baseData?.rewardId as string);
	const bundleUpRewards = useRewardsById(bundleUpData?.rewardId as string);
	const [loyaltyPoints, setLoyaltyPoints] = useState<number>(0);
	const [upsellRewards, setUpsellRewards] = useState<DisplayReward[]>([]);
	const { store } = useAppSelector(selectWebStoreState);
	const bundleIcon = store?.globalImages?.find(
		(image) => image.id === "upsell_reward_icon"
	)?.imageUrl;

	const baseOfferDescription = useMemo(() => {
		if (!baseData || !baseRewards?.length) {
			return "";
		}
		const rewardsDesc = sortBundleRewards(baseRewards)
			.map(
				(r) =>
					`${r.description} ${
						Number(r.description?.replace(/,/g, "")) > 1
							? r.pluralLabel
							: r.singularLabel
					}`
			)
			.join(", ");
		if (baseData.loyaltyPointReward) {
			const prefix = baseRewards.length > 1 ? "and" : "And";
			return `${
				baseRewards.length > 1 ? rewardsDesc + " " : ""
			}${prefix} ${baseData.loyaltyPointReward.toLocaleString(
				"en-US"
			)} Loyalty Points`;
		}
		return rewardsDesc;
	}, [baseData, baseRewards]);

	const getRewardDifferences = () => {
		const result: DisplayReward[] = [];
		const noMatchedRewards: DisplayReward[] = [];
		bundleUpRewards.forEach((bundleReward) => {
			const baseReward = baseRewards.find((br) => br.id === bundleReward.id);
			if (baseReward) {
				const bundleDesc = Number(bundleReward.description?.replace(/,/g, ""));
				const baseDesc = Number(baseReward.description?.replace(/,/g, ""));
				const difference = bundleDesc - baseDesc;

				if (difference) {
					result.push({
						...bundleReward,
						description: difference.toLocaleString("en-US")
					});
				}
			} else {
				noMatchedRewards.push(bundleReward);
			}
		});

		return result.concat(noMatchedRewards);
	};

	const handleOnEnd = () => {
		setTimeout(() => {
			dispatch(onSetTimers({ endTime: undefined }));
			dispatch(onSetBundleUpDip(undefined));
			reportEvent("dialog_closed", {
				source: "bundle_up",
				close_source: "timeout",
				total_time: `${baseData?.upsellOfferConfig?.timerDurationMinutes} minutes`,
				base_offer_id: baseData?.id,
				upsell_offer_id: baseData?.upsellOfferConfig?.upsellOfferId
			});
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "dialog_closed",
				event_type: "web_bundle_up",
				action: "dialog_closed",
				source: "bundle_up",
				offer_id: baseData?.id,
				progress_goal: baseData?.upsellOfferConfig?.upsellOfferId,
				type: "timeout",
				time_total: baseData?.upsellOfferConfig?.timerDurationMinutes
			});
			close({ variant: `bundle-up-info::${id}` });
			close({ variant: `bundle-up::${id}` });
		}, 1000);
	};

	const cbClose = () => {
		dispatch(onSetTimers({ endTime: undefined }));
		dispatch(onSetBundleUpDip(undefined));
		if (baseData?.type === "singleItem") {
			dispatch(onDIPtrigger({ triggerType: "cache-mode" }));
		}
		dispatch(offersApi.util.invalidateTags(["offersTag", "featureTrackerTag"]));
		handleRefresh();
	};

	const onClickBuy = async (isUpsale: boolean) => {
		if (baseData && baseData.upsellOfferConfig && bundleUpData) {
			const upsellOfferId = baseData.upsellOfferConfig.upsellOfferId;
			const offerId = isUpsale ? upsellOfferId : baseData.id;
			const upsellSourceOfferId = baseData.id;
			const upsellConfigId = baseData.upsellOfferConfig.configId;
			const invoice = await wsStoreBuy({
				offerId,
				...(!isUpsale && {
					upsellOfferId
				}),
				...(isUpsale && {
					upsellConfigId,
					upsellSourceOfferId
				}),
				...(dip?.id && {
					dipId: dip.id
				})
			});
			if (invoice) {
				const timestampMs = new Date().getTime();
				close({ variant: `bundle-up::${id}` });
				openDialog(WsDialogs.XSOLLA_PURCHASE, {
					xsollaToken: invoice.xsollaToken,
					xsollaHost: invoice.xsollaHost,
					invoice,
					source: dip ? "dip" : baseData.type,
					triggerType: isUpsale
						? bundleUpRewards?.[0]?.id
						: baseRewards?.[0]?.id,
					pricePoint: isUpsale ? bundleUpData.price : baseData.price,
					cbClose,
					suppressOrderConfirmation,
					postUserMetadata,
					showClose: true,
					suppressDips: baseData?.rebateConfig
						? {
								purchaseSuccess: true,
								purchaseFailed: true,
								purchaseCancelled: true
						  }
						: undefined,
					onPurchaseStarted: (data) => {
						const minutesLeft = getTimeLeft(nowTime, endTime);
						reportEvent("purchase_started", {
							tuid: userTracker?.id as string,
							offer_id: offerId,
							reward_id: isUpsale
								? baseData.upsellOfferConfig?.upsellOfferId
								: baseData.rewardId,
							priority: baseData.priority,
							upsell: isUpsale,
							price_in_cents: Math.round(
								isUpsale ? bundleUpData.price * 100 : baseData.price * 100
							),
							campaign_group: baseData.campaignGroup,
							section_name: baseData.sectionName,
							only_non_payers: baseData.onlyNonPayers,
							loyalty_point_crossout:
								typeof baseData.loyaltyPointCrossout !== "undefined",
							items_crossout:
								baseData.type === "singleItem"
									? typeof (baseData as IShopPurchaseOfferSingleItem)
											.rewardCrossout !== "undefined"
									: false,
							invoice_id: invoice?.id,
							paystation_id: data?.payStation
						});
						reportPlaygamiEvent("tp.web_ga.purchase_flow", {
							event_name: "purchase_started",
							event_type: "web_paystation_purchase",
							action: "purchase_started",
							section_name: "bundle_up",
							purchase_flow_id: data?.payStation,
							session_engaged: authToken?.value ? "1" : "0",
							loyalty_point_crossout: isUpsale
								? !!bundleUpData?.loyaltyPointCrossout
								: !!baseData?.loyaltyPointCrossout,
							price_in_cents: Math.round(invoice?.price * 100),
							page_location: window.location.href,
							engagement_time_msec: (
								Number(data?.data?.milliseconds) - timestampMs
							).toString(),
							reward_id: invoice?.rewardId,
							items_crossout:
								baseData?.type === "singleItem"
									? typeof (baseData as IShopPurchaseOfferSingleItem)
											.rewardCrossout !== "undefined"
									: false,
							offer_id: invoice?.offerId,
							ignore_referrer:
								process.env.REACT_APP_WITH_DEBUG === "true" ? "true" : "false",
							minutes_left: minutesLeft,
							page_title: location.pathname.replace("/", ""),
							page_referrer: invoice?.invoiceMetadata?.referer,
							priority: baseData?.priority,
							invoice_id: invoice?.id
						});
					},
					onPurchaseSuccess: (data) => {
						if (dip?.dip.triggerType === "bouncyChestClick") {
							dispatch(onOpenPopUp("bouncy-chest-reward-collected"));
						}
						if (baseData?.type === "singleItem") {
							dispatch(getLimitedDeals());
						}
						if (baseData?.rebateConfig) {
							dispatch(
								featureTrackerAPI.util.invalidateTags([
									{
										type: "featureTrackerTag",
										id: FeatureType.REBATE_OFFER
									}
								])
							);
							dispatch(onOpenPopUp(`rebate-main-popup::${"0"}`));
						}
						reportEvent("purchase_success", {
							invoice_id: invoice?.id
						});
						reportPlaygamiEvent("tp.web_ga.purchase_flow", {
							event_name: "purchase_success",
							event_type: "web_paystation_purchase",
							action: "purchase_success",
							section_name: "bundle_up",
							purchase_flow_id: data?.version,
							session_engaged: authToken?.value ? "1" : "0",
							loyalty_point_crossout: isUpsale
								? !!bundleUpData?.loyaltyPointCrossout
								: !!baseData?.loyaltyPointCrossout,
							price_in_cents: Math.round(invoice?.price * 100),
							page_location: window.location.href,
							engagement_time_msec: (
								Number(data?.milliseconds) - timestampMs
							).toString(),
							reward_id: invoice?.rewardId,
							items_crossout:
								baseData.type === "singleItem"
									? typeof (baseData as IShopPurchaseOfferSingleItem)
											.rewardCrossout !== "undefined"
									: undefined,
							offer_id: invoice?.offerId,
							ignore_referrer:
								process.env.REACT_APP_WITH_DEBUG === "true" ? "true" : "false",
							page_title: location.pathname.replace("/", ""),
							page_referrer: invoice?.invoiceMetadata?.referer,
							priority: baseData?.priority,
							invoice_id: invoice?.id,
							transaction_id: data?.purchase_invoice_id?.toString()
						});
					}
				});
			}
		}
	};

	useEffect(() => {
		if (
			baseData &&
			baseData.upsellOfferConfig &&
			!isSuccessUpdateCooldown &&
			!isLoadingUpdateCooldown
		) {
			UpdateCooldown({
				upsellConfigId: baseData.upsellOfferConfig.configId,
				upsellOfferId: baseData.upsellOfferConfig.upsellOfferId
			});
		}
	}, [baseData]);

	useEffect(() => {
		if (!bundleUpData) {
			dispatch(onSetBundleUpDip(undefined));
			close({ variant: `bundle-up-info::${id}` });
			close({ variant: `bundle-up::${id}` });
			return;
		}
		if (baseData && baseData.upsellOfferConfig) {
			if (bundleUpData.loyaltyPointReward) {
				let result = 0;
				if (baseData.loyaltyPointReward) {
					result =
						bundleUpData.loyaltyPointReward - baseData.loyaltyPointReward;
				} else {
					result = bundleUpData.loyaltyPointReward;
				}
				setLoyaltyPoints(result);
			}
			reportEvent("dialog_shown", {
				source: "bundle_up",
				feature_type: dip ? "dip" : baseData.type,
				base_offer_id: baseData.id,
				upsell_offer_id: baseData.upsellOfferConfig.upsellOfferId,
				base_reward_id: baseData.rewardId,
				upsell_reward_id: bundleUpData.rewardId,
				priority: baseData.priority,
				base_price_in_cents: Math.round(baseData.price * 100),
				upsell_price_in_cents: Math.round(bundleUpData.price * 100),
				campaign_group: baseData.campaignGroup,
				section_name: baseData.sectionName,
				only_non_payers: baseData.onlyNonPayers,
				loyalty_point_crossout:
					typeof baseData.loyaltyPointCrossout !== "undefined",
				items_crossout:
					baseData.type === "singleItem"
						? typeof (baseData as IShopPurchaseOfferSingleItem)
								.rewardCrossout !== "undefined"
						: undefined
			});
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "dialog_shown",
				event_type: "web_bundle_up",
				action: "dialog_shown",
				source: "bundle_up",
				section_name: baseData?.sectionName,
				offer_id: baseData?.id,
				type: dip ? "dip" : baseData?.type,
				time_total: baseData?.upsellOfferConfig?.timerDurationMinutes,
				reward_id: baseData?.rewardId,
				price_in_cents: Math.round(baseData?.price * 100),
				value_int: Math.round(bundleUpData?.price * 100),
				progress_goal: baseData?.upsellOfferConfig?.upsellOfferId,
				campaign_group: baseData?.campaignGroup
			});
		}
	}, [bundleUpData]);

	useEffect(() => {
		if (bundleUpRewards.length > 0 && baseRewards.length > 0) {
			setUpsellRewards(getRewardDifferences());
		}
	}, [bundleUpRewards, baseRewards]);

	if (
		!baseData ||
		!baseData.upsellOfferConfig ||
		!bundleUpData ||
		upsellRewards.length === 0
	) {
		return null;
	}

	return (
		<Container scale={scale}>
			{endTime && (
				<CountdownV2
					text="Offer ends in:"
					startTime={nowTime}
					endTime={endTime}
					onEnd={handleOnEnd}
					style={{
						fontSize: "1em",
						top: "0.5em"
					}}
				/>
			)}
			<BonusFlagComponent
				text="BUNDLE UP!"
				position="right"
				bannerStyle={bannerStyle}
				textStyle={textStyle}
			/>
			{showOfferId && (
				<DataOverlay
					cooldownMinutes={baseData.upsellOfferConfig.cooldownMinutes}
				/>
			)}
			<Body>
				<div>
					<Title>Exclusive offer of the day!</Title>
					<Title
						fontFamily="Inter"
						fontSize="1.5em"
						fontWeight={700}
						color="#333"
					>
						Upgrade now for a BETTER DEAL
					</Title>
				</div>
				<Content>
					<div>
						<SubTitle style={{ fontSize: "1.8em" }}>You are getting:</SubTitle>
						<OfferContainer
							type={baseData.type}
							style={{ height: isPortrait ? "auto" : "5.7em" }}
						>
							<InfoButton
								onClick={() => {
									open({
										variant: `bundle-up-info::${id}`,
										id: `bundle-up-info::${id}`,
										replaceIfExists: true
									});
								}}
							>
								<InfoIcon width={25} height={25} />
							</InfoButton>
							<div>
								<img
									src={
										baseRewards.length > 1
											? bundleIcon
											: baseRewards?.[0]?.imageUrl
									}
									style={{ height: "4.5em" }}
									alt={
										baseRewards.length > 1
											? baseData.description
											: Number(
													baseRewards?.[0]?.description?.replace(/,/g, "")
											  ) > 1
											? baseRewards?.[0]?.pluralLabel
											: baseRewards?.[0]?.singularLabel
									}
								/>
								<div>
									<SubTitle style={{ fontSize: "1.6em" }}>
										{baseRewards.length > 1
											? baseData.description
											: baseRewards?.[0]?.description}{" "}
										{baseRewards.length > 1
											? ""
											: Number(
													baseRewards?.[0]?.description?.replace(/,/g, "")
											  ) > 1
											? baseRewards?.[0]?.pluralLabel
											: baseRewards?.[0]?.singularLabel}
									</SubTitle>
									<RewardDescription>
										{baseOfferDescription
											.substring(0, descriptionCharLength)
											.trimEnd()}
										{baseOfferDescription.length > descriptionCharLength
											? "..."
											: ""}
									</RewardDescription>
								</div>
							</div>
							<div>
								<span>{baseData.price.toLocaleString("en-US")} USD</span>
							</div>
						</OfferContainer>
					</div>
					<div>
						<SubTitle style={{ fontSize: "1.8em" }}>
							And you also can get:
						</SubTitle>
						<OfferContainer
							border="0.2em solid #FBBD05"
							lineColor="#FBBD0580"
							background="linear-gradient(180deg, #FFF 0%, #FFECB5 100%)"
							type="multiItem"
							style={{ height: "5.7em" }}
						>
							<div
								style={{
									width: "70.5%",
									justifyContent: "center",
									gap: upsellRewards.length > 4 ? "0.8em" : "1em"
								}}
							>
								{sortBundleRewards(upsellRewards).map((reward, index) => {
									return (
										<div key={reward.id}>
											<img
												key={index}
												src={reward.imageUrl}
												style={{
													width: "auto",
													height:
														upsellRewards.length > 1
															? `${
																	20 /
																	(upsellRewards.length +
																		(loyaltyPoints > 0 ? 1 : 0))
															  }em`
															: "3.9em",
													maxHeight: "3.9em"
												}}
												alt={
													Number(reward.description?.replace(/,/g, "")) > 1
														? reward.pluralLabel
														: reward.singularLabel
												}
											/>
											<SubTitle
												style={{
													fontSize: "1.7em",
													textAlign: "center"
												}}
											>
												+
												{reward.description &&
													new Intl.NumberFormat("en-US", {
														notation: "compact",
														compactDisplay: "short",
														maximumFractionDigits: 1,
														roundingMode: "floor"
													}).format(
														Number(reward.description.replaceAll(",", ""))
													)}
											</SubTitle>
										</div>
									);
								})}
								{loyaltyPoints > 0 && (
									<div>
										<img
											src={LPIcon}
											alt="loyalty points icon"
											style={{
												width: "auto",
												height:
													upsellRewards.length > 1
														? `${20 / (upsellRewards.length + 1)}em`
														: "3.9em",
												maxHeight: "3.9em"
											}}
										/>
										<SubTitle
											style={{
												fontSize: "1.7em",
												textAlign: "center"
											}}
										>
											+{loyaltyPoints.toLocaleString("en-US")}
										</SubTitle>
									</div>
								)}
							</div>
							<div
								className={bundleUpData.priceCrossout ? "price-crossout" : ""}
							>
								{bundleUpData.priceCrossout && (
									<span>{bundleUpData.priceCrossout} USD</span>
								)}
								<span>
									{(bundleUpData.price - baseData.price).toLocaleString(
										"en-US"
									)}{" "}
									USD
								</span>
							</div>
						</OfferContainer>
					</div>
				</Content>
				<div style={{ textAlign: "center" }}>
					<FinalPriceSVG
						key={Math.random()}
						text={`All for ${bundleUpData.price.toLocaleString("en-US")} USD`}
						fontSize={"3em"}
					/>
				</div>
				<Content isButton={true}>
					<ThemeButton
						onClick={() => onClickBuy(true)}
						sx={{
							width: "90%",
							margin: "0 auto",
							position: "relative",
							fontSize: "1.6em"
						}}
					>
						<BestValueBadge
							backgroundImage={badge}
							style={{
								width: "6.6em",
								height: "6.6em",
								right: isPortrait ? "-1.7em" : "-2.8em"
							}}
						>
							<p>{bundleUpData.badgeText}</p>
							<span>more</span>
						</BestValueBadge>
						YES, power me up!
					</ThemeButton>
					<ThemeButton
						themeColor="secondary"
						onClick={() => onClickBuy(false)}
						sx={{ width: "90%", margin: "0 auto", fontSize: "1.6em" }}
					>
						Keep original deal
					</ThemeButton>
				</Content>
				{/*
					{isPoisActive && (
						<ContributeToPB>Contribute to Poi’s Bounty</ContributeToPB>
					)}
				*/}
			</Body>
		</Container>
	);
};
